import { clamp } from '../../LocalEmotionUtils';

export interface EyeReliabilityContext {
  eyeReliability: number;
  eyeOccluded: boolean;
  leftVisibility: number;
  rightVisibility: number;
  ambientLightNorm: number;
  lowLightSeverity: number;
  visibilitySource: 'detector' | 'presence-fallback' | 'blendshape-fallback' | 'mixed-fallback';
}

interface EyeContextAnalyzerConfig {
  smoothAlpha: number;
  presenceFallbackMin: number;
}

/**
 * Single-responsibility analyzer for eye-region reliability and occlusion context.
 *
 * It owns temporal smoothing state and low-light adaptive fallback handling,
 * while the classifier remains focused on emotion inference orchestration.
 */
export class EyeContextAnalyzer {
  private lastEyeReliability: number | null = null;
  private lastEyeOccluded: boolean | null = null;

  constructor(private readonly config: EyeContextAnalyzerConfig) {}

  reset(): void {
    this.lastEyeReliability = null;
    this.lastEyeOccluded = null;
  }

  analyze(
    landmarks: Array<{ x: number; y: number; z: number; i?: number; visibility?: number }> | undefined,
    blendshapes: Record<string, number> | undefined,
  ): EyeReliabilityContext {
    const ambientLightNormRaw = (() => {
      const norm = blendshapes?.__ambientLightNorm;
      if (typeof norm === 'number' && Number.isFinite(norm)) return norm;
      const luma = blendshapes?.__ambientLuma;
      if (typeof luma === 'number' && Number.isFinite(luma)) return luma / 255;
      return 0.5;
    })();
    const ambientLightNorm = clamp(ambientLightNormRaw, 0, 1);
    const lowLightSeverity = clamp((0.45 - ambientLightNorm) / 0.30, 0, 1);
    // Presence floor adapts upward in low light; used both for fallback assignment
    // and as the anchor for occlusion thresholds so they are always self-consistent.
    const adaptivePresenceFloor = this.config.presenceFallbackMin * (1 + lowLightSeverity * 0.22);

    const indexed = new Map<number, any>();
    if (Array.isArray(landmarks)) {
      for (let idx = 0; idx < landmarks.length; idx++) {
        const lm: any = landmarks[idx];
        if (!lm) continue;
        const key = typeof lm.i === 'number' ? lm.i : idx;
        indexed.set(key, lm);
      }
    }

    const vis = (i: number): number => {
      const lm = indexed.get(i);
      if (!lm) return 0;
      const v = (lm as any).visibility ?? (lm as any).presence ?? (lm as any).inView ?? (lm as any).score;
      if (typeof v === 'number' && Number.isFinite(v)) return clamp(v, 0, 1);
      if (typeof (lm as any).visibility === 'boolean') return (lm as any).visibility ? 1 : 0;
      if (typeof (lm as any).presence === 'boolean') return (lm as any).presence ? 1 : 0;
      if (typeof lm.x === 'number' && typeof lm.y === 'number') return 1;
      return 0;
    };

    let leftVisibility = (vis(33) + vis(159) + vis(145) + vis(133)) / 4;
    let rightVisibility = (vis(263) + vis(386) + vis(374) + vis(362)) / 4;

    const countPresent = (indices: number[]) => indices.reduce((c, idx) => {
      const lm = indexed.get(idx);
      return c + ((lm && Number.isFinite(lm.x) && Number.isFinite(lm.y)) ? 1 : 0);
    }, 0);

    const leftPresent = countPresent([33, 159, 145, 133]);
    const rightPresent = countPresent([263, 386, 374, 362]);

    let leftUsedPresenceFallback = false;
    let rightUsedPresenceFallback = false;

    if (leftVisibility < 0.025 && leftPresent > 0) {
      const presenceRatio = leftPresent / 4;
      leftVisibility = Math.max(leftVisibility, Math.min(1, presenceRatio * adaptivePresenceFloor));
      leftUsedPresenceFallback = true;
    }
    if (rightVisibility < 0.025 && rightPresent > 0) {
      const presenceRatio = rightPresent / 4;
      rightVisibility = Math.max(rightVisibility, Math.min(1, presenceRatio * adaptivePresenceFloor));
      rightUsedPresenceFallback = true;
    }

    const eyeSquintL = blendshapes?.eyeSquintLeft ?? 0;
    const eyeSquintR = blendshapes?.eyeSquintRight ?? 0;
    const eyeWideL = blendshapes?.eyeWideLeft ?? 0;
    const eyeWideR = blendshapes?.eyeWideRight ?? 0;
    const bsAvg = (Math.max(eyeSquintL, eyeWideL) + Math.max(eyeSquintR, eyeWideR)) / 2;

    let usedBlendshapeFallback = false;
    if ((leftVisibility + rightVisibility) / 2 < 0.02 && bsAvg > 0.02) {
      leftVisibility = Math.max(leftVisibility, Math.min(0.6, bsAvg * 0.9));
      rightVisibility = Math.max(rightVisibility, Math.min(0.6, bsAvg * 0.9));
      usedBlendshapeFallback = true;
    }

    const visibilityAvg = (leftVisibility + rightVisibility) / 2;
    const symmetryPenalty = clamp((Math.abs(eyeSquintL - eyeSquintR) + Math.abs(eyeWideL - eyeWideR)) * 0.35, 0, 0.25);
    let eyeReliability = clamp(visibilityAvg - symmetryPenalty, 0, 1);

    if (this.lastEyeReliability !== null) {
      eyeReliability = clamp(this.lastEyeReliability * (1 - this.config.smoothAlpha) + eyeReliability * this.config.smoothAlpha, 0, 1);
    }
    this.lastEyeReliability = eyeReliability;

    const fallbackFloorPair = leftUsedPresenceFallback
      && rightUsedPresenceFallback
      && leftVisibility <= adaptivePresenceFloor + 0.01
      && rightVisibility <= adaptivePresenceFloor + 0.01;

    const eyeOccluded = (() => {
      if (!fallbackFloorPair) return eyeReliability < 0.58;
      // Both eyes are pinned to the presence floor, meaning visibility data is
      // unreliable (glasses, occlusion, dark room). Anchor occlusion thresholds
      // to the adaptive floor so they are always above the floor value —
      // regardless of whether an occluded calibration profile exists yet.
      if (this.lastEyeOccluded === true)  return eyeReliability < adaptivePresenceFloor + 0.06;
      if (this.lastEyeOccluded === false) return eyeReliability < adaptivePresenceFloor + 0.01;
      return eyeReliability < adaptivePresenceFloor + 0.03;
    })();

    const usedPresenceFallback = leftUsedPresenceFallback || rightUsedPresenceFallback;
    const visibilitySource: EyeReliabilityContext['visibilitySource'] =
      usedPresenceFallback && usedBlendshapeFallback
        ? 'mixed-fallback'
        : usedBlendshapeFallback
          ? 'blendshape-fallback'
          : usedPresenceFallback
            ? 'presence-fallback'
            : 'detector';

    this.lastEyeOccluded = eyeOccluded;

    return {
      eyeReliability,
      eyeOccluded,
      leftVisibility,
      rightVisibility,
      ambientLightNorm,
      lowLightSeverity,
      visibilitySource,
    };
  }
}
